<!-- ============================================================================
     OUR PORTFOLIO — premium product showcase
     Hero → pill filters → screenshot-first grid → gradient CTA (footer is global)
     ========================================================================= -->

<section class="pf-hero" aria-labelledby="pf-hero-title">
  <!-- ambient gradients + floating elements -->
  <div class="pf-hero__deco" aria-hidden="true">
    <span class="pf-blob pf-blob--pink"></span>
    <span class="pf-blob pf-blob--purple"></span>
    <span class="pf-blob pf-blob--indigo"></span>
    <span class="pf-mesh pf-mesh--left"></span>
    <span class="pf-mesh pf-mesh--right"></span>
    <span class="pf-ring"></span>
    <span class="pf-float" style="--x: 12%; --y: 26%; --d: 0s"></span>
    <span class="pf-float pf-float--purple" style="--x: 86%; --y: 18%; --d: 1.4s"></span>
    <span class="pf-float" style="--x: 78%; --y: 72%; --d: 2.6s"></span>
    <span class="pf-float pf-float--purple" style="--x: 18%; --y: 78%; --d: 3.4s"></span>
  </div>

  <div class="pf-shell">
    <header class="pf-hero__head">
      <span class="pf-eyebrow" data-pf-reveal>Our Portfolio</span>

      <h1 class="pf-hero__title" id="pf-hero-title" data-pf-reveal>
        Built, shipped and <span class="pf-grad">running</span>
      </h1>

      <p class="pf-hero__lede" data-pf-reveal>
        Websites, mobile apps and social campaigns we have delivered for teams
        across India and beyond.
      </p>
    </header>

    <!-- ------------------------------------------------------------ filters -->
    <div
      class="pf-tabs"
      role="tablist"
      aria-label="Filter projects by category"
      data-pf-reveal
    >
      <div class="pf-tabs__track">
        <button
          *ngFor="let tab of tabs; let i = index"
          type="button"
          class="pf-tabs__pill"
          role="tab"
          [class.is-active]="activeFilter === tab.id"
          [attr.aria-selected]="activeFilter === tab.id"
          [attr.tabindex]="activeFilter === tab.id ? 0 : -1"
          (click)="setFilter(tab.id)"
          (keydown)="onTabKeydown($event, i)"
        >
          <span class="pf-tabs__label">{{ tab.label }}</span>
        </button>
      </div>
    </div>
  </div>
</section>

<!-- ------------------------------------------------------------------- work -->
<section class="pf-work" aria-label="Selected projects">
  <div class="pf-work__deco" aria-hidden="true">
    <span class="pf-blob pf-blob--soft-pink"></span>
    <span class="pf-blob pf-blob--soft-purple"></span>
  </div>

  <div class="pf-shell">
    <div class="pf-grid" [@pfGrid]="gridState">
      <article
        class="pf-card"
        *ngFor="let project of visibleProjects; trackBy: trackByProject"
      >
        <span class="pf-card__glow" aria-hidden="true"></span>

        <div class="pf-card__shot">
          <img
            class="pf-card__img"
            [src]="project.image"
            [alt]="project.title + ' — ' + project.category + ' project by ResilienceSoft'"
            loading="lazy"
            decoding="async"
          />
          <span class="pf-card__sheen" aria-hidden="true"></span>
          <span class="pf-card__badge">{{ project.category }}</span>
        </div>

        <div class="pf-card__body">
          <h3 class="pf-card__name">{{ project.title }}</h3>
          <p class="pf-card__desc">{{ project.details }}</p>
        </div>

        <a
          *ngIf="project.link"
          class="pf-card__link"
          [href]="project.link"
          target="_blank"
          rel="noopener noreferrer"
          [attr.aria-label]="'Open ' + project.title + ' in a new tab'"
        ></a>
      </article>
    </div>

    <!-- skeletons while Firebase resolves, keeps the grid rhythm intact -->
    <div class="pf-grid pf-grid--skeleton" *ngIf="loading" aria-hidden="true">
      <div class="pf-skeleton" *ngFor="let i of [1, 2, 3, 4, 5, 6]">
        <span class="pf-skeleton__shot"></span>
        <span class="pf-skeleton__line pf-skeleton__line--title"></span>
        <span class="pf-skeleton__line"></span>
        <span class="pf-skeleton__line pf-skeleton__line--short"></span>
      </div>
    </div>

    <div class="pf-empty" *ngIf="!loading && !visibleProjects.length">
      <span class="pf-empty__ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="4"
            width="18"
            height="14"
            rx="2.5"
            stroke="currentColor"
            stroke-width="1.8"
          />
          <path d="M3 8.5h18M8 21h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </span>
      <p class="pf-empty__title">Nothing to show here yet</p>
      <p class="pf-empty__text">
        We are polishing the work for this category. Try another filter in the
        meantime.
      </p>
    </div>
  </div>
</section>

<!-- -------------------------------------------------------------------- CTA -->
<section class="pf-cta" aria-labelledby="pf-cta-title">
  <div class="pf-shell">
    <div class="pf-cta__panel" data-pf-reveal>
      <span class="pf-cta__mesh" aria-hidden="true"></span>
      <span class="pf-cta__blob pf-cta__blob--1" aria-hidden="true"></span>
      <span class="pf-cta__blob pf-cta__blob--2" aria-hidden="true"></span>
      <span class="pf-cta__dot" style="--x: 14%; --y: 28%; --d: 0s" aria-hidden="true"></span>
      <span class="pf-cta__dot" style="--x: 34%; --y: 76%; --d: 1.1s" aria-hidden="true"></span>
      <span class="pf-cta__dot" style="--x: 66%; --y: 22%; --d: 2.2s" aria-hidden="true"></span>
      <span class="pf-cta__dot" style="--x: 88%; --y: 66%; --d: 3s" aria-hidden="true"></span>

      <div class="pf-cta__inner">
        <span class="pf-cta__ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M21 3L3 10.5l7 2.5 2.5 7L21 3z"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10 13l4.5-4.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </span>

        <div class="pf-cta__copy">
          <span class="pf-cta__eyebrow">Have a project in mind?</span>
          <h2 class="pf-cta__title" id="pf-cta-title">
            Let's Build Something Amazing Together
          </h2>
          <p class="pf-cta__text">
            We're always open to discussing new ideas, partnerships and
            opportunities.
          </p>
        </div>

        <a routerLink="/contact-us" class="pf-cta__btn">
          <span>Let's Talk</span>
          <span class="pf-cta__btn-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h13M13 6l6 6-6 6"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </a>
      </div>
    </div>
  </div>
</section>
